import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { BillingEntryDto, OpsBillingDto } from '@lib/dto/billing.dto'
import type { GrantCreditInput } from '@lib/input/billing.input'
import type { Uuid } from '@lib/validators/params'
import { billingKeys, opsTenantKeys } from '@/hooks/queryKeys'
import { opsBillingService } from '@/services/OpsBillingService'

export function useOpsBilling(tenantId: Uuid): UseQueryResult<OpsBillingDto> {
  return useQuery({ queryKey: billingKeys.ops(tenantId), queryFn: () => opsBillingService.summary(tenantId) })
}

/** Grant credit and bill or stop billing. Each refreshes the billing view and the activity log. */
export function useOpsBillingActions(tenantId: Uuid): {
  grant: UseMutationResult<BillingEntryDto, Error, GrantCreditInput>
  setExemption: UseMutationResult<OpsBillingDto, Error, boolean>
} {
  const queryClient = useQueryClient()
  const onSuccess = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: billingKeys.all }),
      queryClient.invalidateQueries({ queryKey: opsTenantKeys.activity(tenantId) }),
    ])
  }
  return {
    grant: useMutation({ mutationFn: (input) => opsBillingService.grant(tenantId, input), onSuccess }),
    setExemption: useMutation({ mutationFn: (exempt) => opsBillingService.setExemption(tenantId, exempt), onSuccess }),
  }
}
